/**
 * FiTopClientsMarginTable — sortable table of ClientMarginRow, owns the
 * click-to-open EntityDetailModal interaction.
 */
import { useMemo, useState } from 'react'
import type { ClientMarginRow } from './fiTypes'
import { formatCurrency, formatPct, marginTone, Sparkline } from './fiFormat'
import EntityDetailModal from './EntityDetailModal'

interface FiTopClientsMarginTableProps {
  rows: ClientMarginRow[]
  /** Period labels aligned oldest→newest with each row.trend, for the detail modal. */
  periodLabels?: string[]
}

type SortKey = 'name' | 'revenue' | 'cogs' | 'grossProfit' | 'marginPct'
type SortDir = 'asc' | 'desc'

const OFFERING_LABEL: Record<ClientMarginRow['offeringMix'], string> = {
  package: 'Paquete',
  consulting: 'Consultoría',
  mixed: 'Mixto',
}
const OFFERING_TONE: Record<ClientMarginRow['offeringMix'], string> = {
  package: 'bg-blue-500/20 text-blue-300',
  consulting: 'bg-purple-500/20 text-purple-300',
  mixed: 'bg-amber-500/20 text-amber-300',
}

function SortHeader({ label, sortKey, activeKey, dir, onSort, align = 'left' }: {
  label: string
  sortKey: SortKey
  activeKey: SortKey
  dir: SortDir
  onSort: (key: SortKey) => void
  align?: 'left' | 'right'
}) {
  const active = sortKey === activeKey
  return (
    <th
      className={`px-4 py-3 font-medium cursor-pointer select-none hover:text-white transition-colors ${align === 'right' ? 'text-right' : 'text-left'}`}
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active && <span className="text-accent">{dir === 'asc' ? '▲' : '▼'}</span>}
      </span>
    </th>
  )
}

export default function FiTopClientsMarginTable({ rows, periodLabels }: FiTopClientsMarginTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('revenue')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [selected, setSelected] = useState<ClientMarginRow | null>(null)

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  const sorted = useMemo(() => {
    const copy = [...rows]
    copy.sort((a, b) => {
      let diff = 0
      if (sortKey === 'name') diff = a.name.localeCompare(b.name)
      else diff = (a[sortKey] ?? 0) - (b[sortKey] ?? 0)
      return sortDir === 'asc' ? diff : -diff
    })
    return copy
  }, [rows, sortKey, sortDir])

  return (
    <div className="bg-gray-800 rounded-xl overflow-hidden">
      <div className="p-4 border-b border-gray-700">
        <h3 className="text-lg font-semibold text-white">Margen por Cliente</h3>
        <p className="text-gray-400 text-sm">Click en una fila para ver el detalle</p>
      </div>

      {sorted.length === 0 ? (
        <div className="text-center py-8 text-gray-400">Sin clientes con revenue en el período.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900/50">
              <tr className="text-left text-gray-400 text-xs uppercase tracking-wide">
                <SortHeader label="Cliente" sortKey="name" activeKey={sortKey} dir={sortDir} onSort={handleSort} />
                <th className="px-4 py-3 font-medium">Tipo</th>
                <SortHeader label="Revenue" sortKey="revenue" activeKey={sortKey} dir={sortDir} onSort={handleSort} align="right" />
                <SortHeader label="COGS" sortKey="cogs" activeKey={sortKey} dir={sortDir} onSort={handleSort} align="right" />
                <SortHeader label="Utilidad" sortKey="grossProfit" activeKey={sortKey} dir={sortDir} onSort={handleSort} align="right" />
                <SortHeader label="Margen" sortKey="marginPct" activeKey={sortKey} dir={sortDir} onSort={handleSort} align="right" />
                <th className="px-4 py-3 font-medium text-center">Tendencia</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50">
              {sorted.map(row => {
                const tone = marginTone(row.marginPct)
                return (
                  <tr
                    key={`${row.entityKind}-${row.entityId}`}
                    className="hover:bg-gray-700/30 cursor-pointer transition-colors"
                    onClick={() => setSelected(row)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">{row.name}</span>
                        {!row.costKnown && (
                          <span className="text-gray-500 text-xs italic" title="Al menos una línea de revenue no tiene costo configurado">
                            sin costo
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          row.isIntragroup ? 'bg-teal-500/20 text-teal-300' : 'bg-slate-500/20 text-slate-300'
                        }`}>
                          {row.isIntragroup ? 'Intra' : 'Externo'}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${OFFERING_TONE[row.offeringMix]}`}>
                          {OFFERING_LABEL[row.offeringMix]}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-white font-semibold">{formatCurrency(row.revenue)}</td>
                    <td className="px-4 py-3 text-right text-gray-300">{formatCurrency(row.cogs)}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={row.grossProfit >= 0 ? 'text-green-400' : 'text-red-400'}>
                        {formatCurrency(row.grossProfit)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-14 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${tone.dot}`}
                            style={{ width: `${Math.max(0, Math.min(100, row.marginPct ?? 0))}%` }} />
                        </div>
                        <span className={`text-xs w-12 text-right font-medium ${tone.text}`}>{formatPct(row.marginPct)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {row.trend && row.trend.length >= 2
                        ? <Sparkline data={row.trend} color="#22d3ee" />
                        : <span className="text-gray-600 text-xs">—</span>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <EntityDetailModal entity={selected} periodLabels={periodLabels} onClose={() => setSelected(null)} />
      )}
    </div>
  )
}
